import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { getGatewayBaseUrl } from "../runtime/config";
import { PosCustomerBenefitGatewayClient } from "../runtime/gateway/customer-benefit-client";
import {
  applyCustomerBenefitRuntime,
  readActiveCustomerBenefits,
} from "../runtime/local-store/customer-benefit-runtime";
import {
  currentCartRuntimeScopeKey,
  readCurrentCartRuntime,
  subscribeCurrentCartRuntime,
} from "../runtime/local-store/current-cart-runtime";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
};

/**
 * Local-first repair boundary for customer benefits.
 *
 * Normal operation still reads the scoped Local Customer Benefit Index immediately and the
 * shared LocalProjectionRuntimeHost continues delta sync in the background. When an operator
 * selects a member whose local wallet is empty, this host performs one non-blocking FULL
 * revalidation from the existing Owner projection. This heals a missed/stale delta cursor
 * without making member selection, reward use, or checkout network-first. A non-empty local
 * wallet never takes this repair path.
 */
export function CustomerBenefitRevalidationHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, session: null });
  const [online, setOnline] = useState(navigator.onLine);
  const attempted = useRef(new Set<string>());
  const currentCartRuntime = useSyncExternalStore(
    subscribeCurrentCartRuntime,
    readCurrentCartRuntime,
    readCurrentCartRuntime,
  );

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({
      next: setRuntime,
      error: () => setRuntime({ terminal: null, session: null }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const terminal = runtime.terminal;
  const session = runtime.session;
  const identity = terminal?.identity ?? null;
  const actor = session?.actor ?? null;
  const scopeKey = identity ? currentCartRuntimeScopeKey(identity) : null;
  const cart = scopeKey && currentCartRuntime.scopeKey === scopeKey ? currentCartRuntime.cart : null;
  const selectedCustomerId = cart?.selectedMember?.customerId ?? null;
  const sameScope = Boolean(
    identity && actor
      && actor.organization_id === identity.organization_id
      && actor.branch_id === identity.branch_id
      && actor.node_id === identity.node_id
      && actor.runtime_session_status === "ACTIVE",
  );
  const baseUrl = getGatewayBaseUrl();
  const client = useMemo(
    () => baseUrl ? new PosCustomerBenefitGatewayClient({ baseUrl }) : null,
    [baseUrl],
  );

  const fullRevalidate = useCallback(async () => {
    if (
      !client
      || !identity
      || !selectedCustomerId
      || !sameScope
      || !online
      || !terminal?.deviceSecret
      || !session?.sessionToken
    ) return;

    const attemptKey = `${identity.organization_id}:${identity.branch_id}:${identity.node_id}:${selectedCustomerId}`;
    if (attempted.current.has(attemptKey)) return;

    const localBenefits = await readActiveCustomerBenefits(identity, selectedCustomerId);
    if (localBenefits.length > 0) return;

    attempted.current.add(attemptKey);

    let cursor = null;
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({
        deviceSecret: terminal.deviceSecret,
        sessionToken: session.sessionToken,
        cursor,
        limit: 500,
      });
      if (!result.ok || !result.data) return;
      await applyCustomerBenefitRuntime(identity, result.data);
      cursor = result.data.next_cursor;
      if (!result.data.has_more) return;
    }
  }, [
    client,
    identity,
    online,
    sameScope,
    selectedCustomerId,
    session?.sessionToken,
    terminal?.deviceSecret,
  ]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    void fullRevalidate().catch(() => undefined);
  }, [fullRevalidate, selectedCustomerId]);

  return null;
}
